import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import MessageActions from './MessageActions';
import './ChatMessage.css';

const ChatMessage = ({ message, from, onRegenerate, messageIndex, timestamp }) => {
  const [showTimestamps, setShowTimestamps] = useState(false);

  useEffect(() => {
    // Load showTimestamps setting
    const loadSettings = () => {
      const savedSettings = localStorage.getItem('chatSettings');
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          setShowTimestamps(settings.showTimestamps || false);
        } catch (error) {
          // Error loading timestamp setting
        }
      }
    };

    loadSettings();

    // Listen for storage changes to update settings in real-time
    const handleStorageChange = (e) => {
      if (e.key === 'chatSettings') {
        loadSettings();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom settings update event
    const handleSettingsUpdate = () => {
      loadSettings();
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('settingsUpdated', handleSettingsUpdate);
    };
  }, []);
  const preprocessMessage = (text) => {
    if (!text || typeof text !== 'string') return text;

    // Remove citation markers
    let processed = text.replace(/【[^】]*】/g, '');

    // Replace display math delimiters \[ and \] with $$...$$
    processed = processed
      .replace(/\\\[/g, '$$')   // Replace \[ with $$
      .replace(/\\\]/g, '$$')   // Replace \] with $$

      // Replace inline math delimiters \( and \) with $...$
      .replace(/\\\(/g, '$')    // Replace \( with $
      .replace(/\\\)/g, '$');   // Replace \) with $

    return processed;
  };

  const remarkMathOptions = {
    singleDollarTextMath: true,  // Enable single dollar for inline math
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return new Date().toLocaleTimeString();
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className={`chat-message ${from}`} data-message-index={messageIndex}>
      <div className="message">
        {showTimestamps && (
          <div className="message-timestamp">
            {formatTimestamp(timestamp)}
          </div>
        )}
        <ReactMarkdown
          className="markdown-content"
          children={preprocessMessage(message)}
          remarkPlugins={[
            [remarkMath, remarkMathOptions], // Enables math processing
            remarkGfm // Enables GitHub Flavored Markdown (tables, strikethrough, etc.)
          ]}
          rehypePlugins={[rehypeRaw, rehypeKatex]} // Enables HTML and KaTeX support
          components={{
            p: ({ children }) => <>{children}</>, // Remove extra <p> tags
            table: ({ children }) => (
              <div className="table-wrapper">
                <table className="markdown-table">{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead className="markdown-thead">{children}</thead>,
            tbody: ({ children }) => <tbody className="markdown-tbody">{children}</tbody>,
            tr: ({ children }) => <tr className="markdown-tr">{children}</tr>,
            th: ({ children }) => <th className="markdown-th">{children}</th>,
            td: ({ children }) => <td className="markdown-td">{children}</td>,
          }}
        />
      </div>
      <MessageActions
        message={message}
        isBot={from === 'bot'}
        onRegenerate={onRegenerate}
      />
    </div>
  );
};

export default ChatMessage;
